import { Button } from '@change/ds-core-ui/Button';
import { Image } from '@change/ds-core-ui/Image';
import { Text } from '@change/ds-core-ui/Text';
import { GrowNurtureIllustration } from '@change/ds-illustrations/GrowNurtureIllustration';
import { cn } from '@change/ds-utils';

import { translationKey } from '@change-corgi/core/i18n';
import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { ActionBar, SecondaryActionButton } from '#src/app/pages/startAPetitionV2/shared/components';

import { useIntro } from './hooks/useIntro';

export const Intro: React.FC = () => {
	const { translate } = useI18n();
	const {
		data: { copy, image },
		actions,
	} = useIntro();
	const getStaticAssetUrl = useGetStaticAssetUrl();

	return (
		<div
			className="container mx-auto mb-22 flex max-w-155 flex-col gap-6 px-4 py-8 md:gap-12 lg:py-20"
			data-testid="start"
		>
			{image ? (
				// The campaign image has the heading drawn in, so it is the heading and its alt text is the heading's name.
				<h1>
					{/* Capped so a large upload doesn't fill the desktop column and push the buttons down. aspect-auto keeps
					the upload's own shape, since the image can be swapped in the FCM without a deploy. */}
					<Image
						className="mx-auto aspect-auto w-full max-w-96"
						src={getStaticAssetUrl(image.path)}
						alt={image.alt}
						// If the image fails to load, its alt text shows in its place, styled like the normal heading.
						fallback={
							<Text variant="heading" size="x-large" className="block text-center">
								{image.alt}
							</Text>
						}
					/>
				</h1>
			) : (
				<GrowNurtureIllustration className="mx-auto" alt={translate('corgi.start_a_petition.intro.image_alt')} />
			)}
			<div>
				{!image && (
					<Text as="h1" variant="heading" size="x-large" className="text-center">
						<Translate value="corgi.start_a_petition.intro.heading" />
					</Text>
				)}
				<Text
					as="p"
					variant="body"
					size="large"
					color="sunrise.semantic.typography.weak"
					className={cn('text-center', { 'mt-4': !image })}
				>
					{copy.description ?? <Translate value="corgi.start_a_petition.intro.description" />}
				</Text>
			</div>

			<ActionBar>
				<div className="flex flex-col gap-4">
					{/* eslint-disable @change-corgi/require-data-qa-attribute */}
					<Button
						variant="primary"
						className="mx-auto w-full md:max-w-75"
						data-qa="primary-action"
						onClick={(e) => actions.submit('with_ai', e.target)}
						type="button"
					>
						{copy.primaryActionLabel ?? <Translate value="corgi.start_a_petition.intro.primary_action_label" />}
					</Button>
					<SecondaryActionButton
						variant="tertiary"
						className="mx-auto w-full md:max-w-75"
						label={translationKey('corgi.start_a_petition.intro.secondary_action_label')}
						onClick={() => actions.submit('write_my_own')}
						type="button"
					/>
					{/* eslint-enable @change-corgi/require-data-qa-attribute */}
				</div>
			</ActionBar>
		</div>
	);
};
